/**
 * useSaveStudyLabel (AIE-539)
 *
 * Wires a study's label editor to the save endpoint with an optimistic cache
 * update: it patches the edited study's `user_label`/`user_label_color` in every
 * cached `["studies", ...]` query and, when a color is explicitly set, recolors
 * every study sharing that label so the category reads consistently at a glance.
 * On settle the studies queries are invalidated so the server (JOINed) truth wins.
 *
 * Also derives `knownLabelColors` from the cache so the editor can preselect an
 * existing category's color when the user types a label that already exists.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { useApi } from "@/hooks/useApi";
import { toast } from "@/hooks/useToast";
import type { StudiesListResponse } from "@/types/api";
import {
  DEFAULT_LABEL_COLOR,
  isLabelColorKey,
  type LabelColorKey,
} from "@/utils/labelColors";

interface SaveLabelVars {
  label: string;
  color: LabelColorKey | null;
}

export function useSaveStudyLabel(studyId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  const knownLabelColors = useMemo(() => {
    const map: Record<string, string> = {};
    const entries = queryClient.getQueriesData<StudiesListResponse>({
      queryKey: ["studies"],
    });
    for (const [, data] of entries) {
      data?.studies?.forEach((s) => {
        const key = s.user_label?.trim().toLowerCase();
        if (key && isLabelColorKey(s.user_label_color)) {
          map[key] = s.user_label_color;
        }
      });
    }
    return map;
    // Recomputed on each render — cards re-render when the studies query updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  const mutation = useMutation({
    mutationFn: ({ label, color }: SaveLabelVars) =>
      api.saveStudyLabel(studyId, label, color),
    onMutate: async ({ label, color }: SaveLabelVars) => {
      await queryClient.cancelQueries({ queryKey: ["studies"] });
      const snapshots = queryClient.getQueriesData<StudiesListResponse>({
        queryKey: ["studies"],
      });

      const trimmed = label.trim();
      const newLabel = trimmed.length ? trimmed : null;
      const knownColor = knownLabelColors[trimmed.toLowerCase()];
      const newColor: LabelColorKey | null = newLabel
        ? (color ??
          (isLabelColorKey(knownColor) ? knownColor : DEFAULT_LABEL_COLOR))
        : null;

      for (const [key, data] of snapshots) {
        if (!data?.studies) continue;
        const next = data.studies.map((s) => {
          if (s.study_id === studyId) {
            return { ...s, user_label: newLabel, user_label_color: newColor };
          }
          // Category recolor: an explicit color change recolors the whole category.
          if (
            newLabel &&
            color &&
            s.user_label?.trim().toLowerCase() === trimmed.toLowerCase()
          ) {
            return { ...s, user_label_color: newColor };
          }
          return s;
        });
        queryClient.setQueryData(key, { ...data, studies: next });
      }

      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, data]) =>
        queryClient.setQueryData(key, data),
      );
      toast({
        title: "Couldn't save label",
        description: "Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["studies"] });
    },
  });

  return {
    knownLabelColors,
    isSaving: mutation.isPending,
    save: (label: string, color: LabelColorKey | null) =>
      mutation.mutate({ label, color }),
  };
}
